import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, ChevronRight, Plus, Trash2, Save, Loader2, Info } from 'lucide-react';
import { sonstigesOhneKommentar } from '../lib/stundenzettelHilfen';

function SonderRow({ entry, index, onChange, onRemove, disabled }) {
  // Bei "Sonstiges" ist die Bemerkung Pflicht - solange sie fehlt, ist das Feld rot.
  const kommentarFehlt = sonstigesOhneKommentar([entry]).length > 0;
  const istSonstiges = String(entry.grund || '').trim() === 'Sonstiges';
  return (
    <div className="flex items-center gap-2" data-testid={'sonder-row-' + index}>
      <Input type="date" value={entry.date} onChange={(e) => onChange(index, 'date', e.target.value)}
        className="w-36 h-9 text-sm" disabled={disabled} data-testid={'sonder-date-' + index} />
      <Input type="number" step="0.25" min="0" value={entry.hours} onChange={(e) => onChange(index, 'hours', e.target.value)}
        className="w-20 h-9 text-sm" placeholder="Std." disabled={disabled} data-testid={'sonder-hours-' + index} />
      <Select value={entry.grund} onValueChange={(v) => onChange(index, 'grund', v)} disabled={disabled}>
        <SelectTrigger className="w-36 h-9 text-sm" data-testid={'sonder-grund-' + index}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Klassenfahrt">Klassenfahrt</SelectItem>
          <SelectItem value="Ausflug">Ausflug</SelectItem>
          <SelectItem value="Sonstiges">Sonstiges</SelectItem>
        </SelectContent>
      </Select>
      <Input value={entry.comment} onChange={(e) => onChange(index, 'comment', e.target.value)}
        className={`flex-1 h-9 text-sm${kommentarFehlt ? ' border-red-500 focus-visible:ring-red-500' : ''}`}
        placeholder={istSonstiges ? 'Bitte beschreiben, wofür (Pflicht)' : 'Bemerkung'}
        disabled={disabled} data-testid={'sonder-comment-' + index} />
      {!disabled && (
        <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-700 flex-shrink-0"
          onClick={() => onRemove(index)} data-testid={'sonder-remove-' + index}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export function SonderstundenSection({ isOpen, onToggle, sonderstunden, onChange, onAdd, onRemove, onSave, saving, disabled, total, pauschale }) {
  const rows = sonderstunden.map((entry, idx) => (
    <SonderRow key={idx} entry={entry} index={idx} onChange={onChange} onRemove={onRemove} disabled={disabled} />
  ));

  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded-lg" data-testid="sonderstunden-section">
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3" data-testid="sonderstunden-toggle">
        <span className="flex items-center gap-2 font-medium text-slate-800">
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Sonderstunden (Klassenfahrt / Ausflug)
          {total > 0 && <span className="text-xs font-normal text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><span translate="no">{total.toFixed(2)}</span> Std.</span>}
        </span>
        <span className="text-xs text-slate-500">optional – nur bei genehmigten Mehrstunden</span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 space-y-2">
          {pauschale && Number(pauschale.hours) > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1.5"
              data-testid="hinweis-pauschale-sonderstunden">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                {`\u201e${pauschale.label}\u201c (`}
                <span translate="no">{Number(pauschale.hours).toFixed(2).replace('.', ',')}</span>
                {' Std. im Monat) wird automatisch abgerechnet. Bitte NICHT hier als Sonderstunde eintragen.'}
              </span>
            </div>
          )}
          {rows}
          {!disabled && (
            <div className="flex items-center gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={onAdd} data-testid="add-sonderstunde">
                <Plus className="h-4 w-4 mr-1" />Sonderstunde hinzufügen
              </Button>
              <Button size="sm" className="bg-blue-800 hover:bg-blue-900" onClick={onSave} disabled={saving} data-testid="save-sonderstunden">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Speichern
              </Button>
            </div>
          )}
          {sonderstunden.length > 0 && (
            <div className="text-sm text-amber-800 font-medium pt-1" data-testid="sonder-total">
              Sonderstunden gesamt: <span translate="no">{total.toFixed(2)}</span> Std. (werden zusätzlich abgerechnet)
            </div>
          )}
          {sonderstunden.length === 0 && (
            <p className="text-xs text-slate-400">Keine Sonderstunden eingetragen.</p>
          )}
        </div>
      )}
    </div>
  );
}
